import { ArrowLeft, Eye, EyeOff, CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { FormButton } from './FormButton';

interface NewPasswordScreenProps {
  onBack: () => void;
  onSuccess: () => void;
}

export const NewPasswordScreen = ({
  onBack,
  onSuccess,
}: NewPasswordScreenProps) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const hasMinLength = newPassword.length >= 8;
  const hasNumber = /\d/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword !== '';

  const isValid = hasMinLength && hasNumber && passwordsMatch;

  const handleSave = () => {
    if (!isValid) {
      if (!passwordsMatch) {
        setError('Las contraseñas no coinciden');
      }
      return;
    }

    setError('');
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      onSuccess();
    }, 1500);
  };

  return (
    <div className="min-h-screen bg-[#F7F8FA]">
      {/* Header */}
      <div className="bg-white border-b border-[#E5E7EB] sticky top-0 z-10">
        <div className="flex items-center gap-3 p-4">
          <button
            onClick={onBack}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-[#F7F8FA] transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-[#111827]" />
          </button>
          <h1 className="text-lg font-bold text-[#111827]">Nueva contraseña</h1>
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-6">
        <div>
          <p className="text-sm text-[#6B7280]">
            Crea una nueva contraseña segura para tu cuenta
          </p>
        </div>

        {/* New Password Input */}
        <div>
          <label className="block text-sm font-medium text-[#111827] mb-2">
            Nueva contraseña
          </label>
          <div className="relative">
            <input
              type={showNewPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 pr-12 border-2 border-[#E5E7EB] rounded-[12px] focus:border-[#FF6A2A] focus:outline-none transition-colors"
              placeholder="Ingresa tu nueva contraseña"
            />
            <button
              type="button"
              onClick={() => setShowNewPassword(!showNewPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827]"
            >
              {showNewPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Confirm Password Input */}
        <div>
          <label className="block text-sm font-medium text-[#111827] mb-2">
            Confirmar nueva contraseña
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                setError('');
              }}
              className="w-full px-4 py-3 pr-12 border-2 border-[#E5E7EB] rounded-[12px] focus:border-[#FF6A2A] focus:outline-none transition-colors"
              placeholder="Confirma tu nueva contraseña"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280] hover:text-[#111827]"
            >
              {showConfirmPassword ? (
                <EyeOff className="w-5 h-5" />
              ) : (
                <Eye className="w-5 h-5" />
              )}
            </button>
          </div>
        </div>

        {/* Password Requirements */}
        <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 space-y-2">
          <p className="text-xs font-medium text-[#111827] mb-3">
            Tu contraseña debe tener:
          </p>
          <div className="flex items-center gap-2">
            {hasMinLength ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-[#E5E7EB]" />
            )}
            <span className={`text-xs ${hasMinLength ? 'text-green-600' : 'text-[#6B7280]'}`}>
              Mínimo 8 caracteres
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasNumber ? (
              <CheckCircle2 className="w-4 h-4 text-green-600" />
            ) : (
              <XCircle className="w-4 h-4 text-[#E5E7EB]" />
            )}
            <span className={`text-xs ${hasNumber ? 'text-green-600' : 'text-[#6B7280]'}`}>
              Al menos 1 número
            </span>
          </div>
          {confirmPassword && (
            <div className="flex items-center gap-2">
              {passwordsMatch ? (
                <CheckCircle2 className="w-4 h-4 text-green-600" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600" />
              )}
              <span className={`text-xs ${passwordsMatch ? 'text-green-600' : 'text-red-600'}`}>
                Las contraseñas coinciden
              </span>
            </div>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-[12px] p-3">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        <FormButton
          onClick={handleSave}
          loading={loading}
          disabled={!isValid}
        >
          Guardar contraseña
        </FormButton>
      </div>
    </div>
  );
};
